import * as React from 'react';
import { cn } from '../../lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../Command';
import { Tag } from '../Tag';
import { Button } from '../Button';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { FloatingLabel, FloatingLabelProps } from '../FloatingLabel';
import { Check, ChevronDown, X } from 'lucide-react';
import { IconSquare, IconSquareCheck } from '@tabler/icons-react';
import useI18n from '../../hooks/useI18n';
import type {
  ComboboxLegacyProps,
  ComboboxLegacySingleProps,
  ComboboxLegacyMultiProps,
} from './types';
import type { DropDownItem } from '../../types/dropDownItem';

/**
 * @deprecated Use the new `Combobox` , `ComboboxMulti`, or the individual composable components instead.
 */
export const Combobox = React.forwardRef<
  HTMLButtonElement,
  ComboboxLegacyProps
>(
  (
    {
      className,
      defaultValue,
      error = false,
      fullWidth = false,
      hideRenderedValue = false,
      itemRenderer,
      items = [],
      labelProps,
      limitTags = false,
      multiSelect = false,
      onOpenChange,
      onSearchChange,
      placeholder,
      placeholderFilter,
      placeholderNoResults,
      search,
      shouldFilter = true,
      showClearButton = false,
      slotProps,
      value,
      filter,
      ...props
    },
    ref,
  ) => {
    const { t } = useI18n();
    const [isOpen, setIsOpen] = React.useState(props.open ?? false);
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const [displayItems, setDisplayItems] = React.useState<DropDownItem[]>([]);
    const valueRef = React.useRef<HTMLDivElement>(null);
    // Preserve a reference to selected items so they can be rendered even
    // when a user-entered search query would filter them out
    const [selectedItemsCache, setSelectedItemsCache] = React.useState(
      () => new Map<string, DropDownItem>(),
    );

    const lookupValue = isControlled ? value : internalValue;

    const selectedKeys = React.useMemo(() => {
      if (multiSelect) {
        return Array.isArray(lookupValue) ? lookupValue : [];
      }

      return typeof lookupValue === 'string' && lookupValue.length > 0
        ? [lookupValue]
        : [];
    }, [lookupValue, multiSelect]);

    const itemsByValue = React.useMemo(() => {
      const map = new Map<string, DropDownItem>();

      items.forEach((item) => {
        map.set(item.value, item);
      });

      return map;
    }, [items]);

    const selectedValue = React.useMemo(() => {
      if (!selectedKeys.length) return [];

      return selectedKeys
        .map((key) => itemsByValue.get(key) ?? selectedItemsCache.get(key))
        .filter((item): item is DropDownItem => Boolean(item));
    }, [itemsByValue, selectedItemsCache, selectedKeys]);

    const sortedSelectedValue = React.useMemo(() => {
      if (!multiSelect || !limitTags) return selectedValue;

      return [...selectedValue].sort((a, b) => a.label.length - b.label.length);
    }, [limitTags, multiSelect, selectedValue]);

    const renderAsSpan = labelProps?.renderAsSpan ?? true;
    const { className: labelClassName, ...restLabelProps } = labelProps ?? {};
    const mergedLabelProps: FloatingLabelProps = {
      ...restLabelProps,
      renderAsSpan,
    };
    const generatedLabelId = React.useId();
    const floatingLabelId = labelProps?.label ? generatedLabelId : undefined;

    const shouldUseSingleValueReadonlyInput =
      !multiSelect && !hideRenderedValue && !itemRenderer;
    const canShowClearButton =
      !props.disabled &&
      showClearButton &&
      selectedValue &&
      selectedValue.length > 0;

    const focusBorderClass =
      labelProps?.float === 'top'
        ? cn(
            'eye:focus:outline-none eye:relative eye:cursor-pointer eye:before:absolute eye:before:rounded-[12px]',
            'eye:before:top-[7px] eye:before:right-[-5px] eye:before:bottom-[-5px] eye:before:left-[-5px]',
            'eye:before:border-2 eye:before:border-transparent eye:focus-within:before:border-black',
          )
        : cn(
            'eye:focus:border-black eye:cursor-pointer eye:rounded-md eye:align-middle',
            'eye:outline-offset-3 eye:outline-black eye:focus-within:outline-2',
          );

    const labelText =
      typeof labelProps?.label === 'string' ? labelProps.label : undefined;
    const comboboxAriaLabel = props['aria-label'] ?? labelText;

    React.useEffect(() => {
      const next = selectedValue.length
        ? new Map(selectedValue.map((item) => [item.value, item]))
        : new Map<string, DropDownItem>();

      setSelectedItemsCache((prev) => (areMapsEqual(prev, next) ? prev : next));
    }, [selectedValue]);

    React.useEffect(() => {
      if (props.open !== undefined) {
        setIsOpen(props.open);
      }
    }, [props.open]);

    React.useEffect(() => {
      setDisplayItems(sortedSelectedValue);
    }, [sortedSelectedValue]);

    React.useLayoutEffect(() => {
      if (!multiSelect || !limitTags) return;

      const el = valueRef.current;
      if (!el) return;
      if (displayItems.length === 0) return;

      const overflowing = el.scrollWidth > el.clientWidth;

      if (overflowing && displayItems.length > 1) {
        // remove one item, next render will re-measure
        setDisplayItems((prev) => {
          if (prev.length <= 1) return prev;
          return prev.slice(0, -1);
        });
      }
    }, [displayItems, limitTags, multiSelect, selectedValue.length]);

    const handleClear = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (multiSelect) {
        if (!isControlled) setInternalValue([]);
        (props as ComboboxLegacyMultiProps).onSelect?.([]);
      } else {
        if (!isControlled) setInternalValue('');
        (props as ComboboxLegacySingleProps).onSelect?.('');
      }
    };

    const handleSelect = (val: string) => {
      if (multiSelect) {
        const prev = Array.isArray(lookupValue) ? lookupValue : [];
        const next = prev.includes(val)
          ? prev.filter((v) => v !== val)
          : [...prev, val];

        if (!isControlled) setInternalValue(next);
        (props as ComboboxLegacyMultiProps).onSelect?.(next);
      } else {
        if (!isControlled) setInternalValue(val);
        (props as ComboboxLegacySingleProps).onSelect?.(val);
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };

    const renderItems =
      itemRenderer ??
      ((item) => (multiSelect ? <Tag>{item.label}</Tag> : item.label));

    const renderValue = () => {
      if (hideRenderedValue) return null;

      if (!selectedValue?.length) {
        return <span className='eye:text-grey-placeholder'>{placeholder}</span>;
      }

      return (
        <>
          {displayItems.map((item, index) => (
            <span
              className={cn(
                // flexbox to detect overflow for multiSelect, truncate
                // for single select value
                multiSelect
                  ? 'eye:flex'
                  : 'eye:max-w-full eye:truncate eye:whitespace-nowrap',
              )}
              key={index}
            >
              {renderItems(item, index)}
            </span>
          ))}
          {selectedValue.length > displayItems.length && (
            <span
              className={cn(
                'eye:flex eye:items-center eye:rounded-full eye:bg-grey-20 eye:text-12',
                'eye:h-24 eye:min-w-24 eye:justify-center eye:px-4',
              )}
              data-testid='combobox-overflow-count'
              aria-hidden={true}
            >
              +{selectedValue.length - displayItems.length}
            </span>
          )}
        </>
      );
    };

    return (
      <Popover
        open={isOpen}
        onOpenChange={(openValue) => {
          if (!props.disabled && props.open === undefined) {
            setIsOpen(openValue);
            onOpenChange?.(openValue);
          }
        }}
        {...slotProps?.popover}
      >
        <PopoverTrigger asChild {...slotProps?.popoverTrigger}>
          <button
            className={cn(
              focusBorderClass,
              props.disabled && 'eye:cursor-not-allowed eye:text-grey-120',
              fullWidth && 'eye:w-full',
              className,
            )}
            ref={ref}
            role='combobox'
            aria-expanded={isOpen}
            aria-label={comboboxAriaLabel}
            aria-disabled={props.disabled}
            aria-required={props.required || undefined}
            tabIndex={props.disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (props.disabled) return;

              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                (e.currentTarget as HTMLElement).click();
              }
            }}
          >
            <FloatingLabel
              {...mergedLabelProps}
              labelId={floatingLabelId}
              labelClassName={cn(
                'eye:overflow-auto',
                multiSelect && !limitTags && 'eye:items-center',
              )}
              className={cn(
                multiSelect && 'eye:w-full eye:min-w-200 eye:cursor-pointer',
                multiSelect && !limitTags && 'eye:h-auto eye:items-center',
                error && 'eye:border-red-100 eye:hover:border-red-100',
                labelClassName,
              )}
              required={props.required}
              disabled={props.disabled}
              rightSlot={
                <>
                  {canShowClearButton && (
                    <Button
                      className='eye:h-24! eye:min-h-24! eye:w-24! eye:min-w-24!'
                      variant='ghost'
                      size='icon'
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      tabIndex={0}
                      onClick={handleClear}
                      asChild
                      aria-label={t('resetInput')}
                    >
                      <div role='button'>
                        <X aria-hidden='true' className='eye:h-16! eye:w-16!' />
                      </div>
                    </Button>
                  )}
                  <div
                    className={cn(
                      props.disabled
                        ? 'eye:disabled eye:text-grey-disabled'
                        : 'eye:cursor-pointer',
                    )}
                    aria-hidden='true'
                  >
                    <ChevronDown />
                  </div>
                </>
              }
            >
              <div
                ref={valueRef}
                data-testid='combobox-value'
                className={cn(
                  'eye:flex eye:w-full eye:items-center eye:gap-4',
                  !multiSelect || (multiSelect && limitTags)
                    ? 'eye:overflow-hidden eye:text-nowrap'
                    : 'eye:flex-wrap',
                )}
              >
                {shouldUseSingleValueReadonlyInput ? (
                  <input
                    title={selectedValue[0]?.label}
                    readOnly
                    aria-readonly='true'
                    aria-labelledby={
                      props['aria-labelledby'] ?? floatingLabelId
                    }
                    value={selectedValue[0]?.label ?? ''}
                    placeholder={placeholder}
                    className={cn(
                      props.disabled
                        ? 'eye:cursor-not-allowed'
                        : 'eye:cursor-pointer',
                      'eye:w-full eye:bg-transparent eye:focus:outline-none',
                      'eye:text-ellipsis eye:placeholder:text-grey-placeholder!',
                    )}
                    onFocus={(e) => {
                      // clicking the input again after the popover is open
                      // allows the user to select the text to, for example,
                      // easily copy it to the clipboard.
                      if (!isOpen && !props.disabled) return;
                      e.currentTarget.select();
                    }}
                  />
                ) : (
                  renderValue()
                )}
              </div>
            </FloatingLabel>
          </button>
        </PopoverTrigger>

        <PopoverContent
          side='bottom'
          avoidCollisions={false}
          align='start'
          matchTriggerWidth
          {...slotProps?.popoverContent}
        >
          <Command
            shouldFilter={shouldFilter}
            filter={filter}
            // A placeholder is not an accessible name. cmdk always points the
            // input's aria-labelledby at its own visually hidden label, which
            // also shadows an aria-label on the input, so name it here.
            label={shouldFilter ? t('search') : undefined}
          >
            {shouldFilter && (
              <CommandInput
                placeholder={placeholderFilter ?? t('searchPlaceholder')}
                value={search}
                onValueChange={onSearchChange}
                className='eye:placeholder:text-grey-placeholder'
              />
            )}
            <CommandList>
              <CommandEmpty>
                {placeholderNoResults ?? t('noResultsFound')}
              </CommandEmpty>
              <CommandGroup>
                {items.map((item, index) => {
                  const isSelected = multiSelect
                    ? lookupValue?.includes(item.value)
                    : lookupValue === item.value;

                  return (
                    <CommandItem
                      key={index}
                      value={item.label}
                      onSelect={() => handleSelect(item.value)}
                      className={cn(isSelected && 'eye:bg-grey-20')}
                    >
                      <span className={'eye:flex eye:items-center eye:gap-4'}>
                        {multiSelect && (
                          <>
                            {isSelected ? (
                              <IconSquareCheck aria-hidden={true} />
                            ) : (
                              <IconSquare aria-hidden={true} />
                            )}
                          </>
                        )}
                        <span
                          className='eye:line-clamp-3 eye:break-all eye:whitespace-normal'
                          title={item.label}
                        >
                          {renderItems(item, index)}
                        </span>
                      </span>
                      {!multiSelect && isSelected && (
                        <Check
                          aria-hidden={true}
                          data-slot='combobox-legacy-item-indicator'
                          className='eye:ml-auto eye:size-16 eye:shrink-0'
                        />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

function areMapsEqual(
  a: Map<string, DropDownItem>,
  b: Map<string, DropDownItem>,
) {
  if (a.size !== b.size) return false;

  for (const [key, value] of a) {
    if (b.get(key) !== value) return false;
  }

  return true;
}

Combobox.displayName = 'Combobox';
